// View layer: builds the shadow-DOM tree and exposes render methods.
// The controller (widget.ts) owns state and wires behavior through UICallbacks.

import { buildStyles } from "./styles";
import type { WidgetConfig } from "./config";
import type { Role, Source, ProductCard, CardVariant } from "./types";

/** "1.4 MB" — short enough to sit under a filename. */
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export interface UICallbacks {
  onSend: (text: string) => void;
  /** Customer picked a file to share. */
  onSendFile?: (file: File) => void;
  /** Customer is typing — throttled by the caller. */
  onTyping?: () => void;
  onToggle: (open: boolean) => void;
  /** Customer clicked the header "Talk to a person" action. */
  onRequestHuman: () => void;
  /** Customer clicked the header expand/shrink toggle. */
  onToggleExpand: () => void;
  /** Customer clicked the header "Start new chat" action. */
  onNewChat: () => void;
  /** Add a variant (case) to the shopper's cart. Resolves to whether it succeeded. */
  onAddToCart: (productId: number, variantId: number, quantity: number) => Promise<{ ok: boolean }>;
}

/** Handle to a streaming bot message — append tokens and attach sources as they arrive. */
export interface BotMessageHandle {
  append: (text: string) => void;
  setSources: (sources: Source[]) => void;
  /** Render clickable add-to-cart cards beneath the message (from the products event). */
  setProducts: (products: ProductCard[]) => void;
  /** Replace the raw streamed text with HTML, converting plain URLs into clickable links. Call once when the stream ends. */
  finalize: () => void;
  /** Render the one-click live-agent button beneath the message. */
  setAgentOffer: (label: string) => void;
  el: HTMLElement;
}

export interface EscalateFormOptions {
  lead: string;
  defaultMessage: string;
  onSubmit: (data: { email: string; name: string; message: string }) => Promise<boolean>;
}

export interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
}

export interface ContactFormData {
  name: string;
  email: string;
  orderRef: string;
}

export interface ContactFormOptions {
  lead: string;
  /** Resolve true to dismiss the form (the caller renders what happens next). */
  onSubmit: (data: ContactFormData) => Promise<boolean>;
}

export interface SignupFormOptions {
  lead: string;
  onSubmit: (data: SignupFormData) => Promise<import("./types").SignupResult>;
}

const ICON_CHAT =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 11.5a8.5 8.5 0 0 1-12.2 7.66L3 21l1.84-5.8A8.5 8.5 0 1 1 21 11.5z" fill="currentColor"/></svg>';
const ICON_CLOSE =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
const ICON_SEND =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 12l16-8-6 16-2.5-6.5L4 12z" fill="currentColor"/></svg>';
const ICON_LINK =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 5h5v5M19 5l-9 9M11 5H6a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ICON_CHECK =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ICON_CART =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 6h15l-1.5 9h-12L6 6zM6 6L5 3H2M9 20a1 1 0 100-2 1 1 0 000 2zm8 0a1 1 0 100-2 1 1 0 000 2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ICON_PLUS =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
const ICON_MINUS =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
const ICON_EXPAND =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ICON_COLLAPSE =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 8V3h5M21 8V3h-5M3 16v5h5M21 16v5h-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ICON_NEWCHAT =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 20h9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /(https?:\/\/[^\s<>"]+)/g;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

const HTML_ESCAPE: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => HTML_ESCAPE[c]);
}

/** Convert plain-text bot output into HTML, auto-linking any http(s) URLs. The
 *  input is treated as untrusted (escaped before linkification) so we never let
 *  the model's tokens become tag-injection vectors. */
function linkifyText(raw: string): string {
  let out = "";
  let last = 0;
  for (const m of raw.matchAll(URL_RE)) {
    const start = m.index!;
    const matched = m[1];
    // Strip trailing punctuation that almost never belongs to the URL.
    const trim = matched.match(/^(.+?)([,.!?:;)\]]*)$/);
    const url = trim ? trim[1] : matched;
    const trail = trim ? trim[2] : "";
    out += escapeHtml(raw.slice(last, start));
    const hrefSafe = url.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
    out += `<a href="${hrefSafe}" target="_blank" rel="noopener noreferrer">${escapeHtml(url)}</a>${escapeHtml(trail)}`;
    last = start + matched.length;
  }
  out += escapeHtml(raw.slice(last));
  return out;
}

function icon(svg: string, className?: string): HTMLSpanElement {
  const span = el("span", className);
  span.innerHTML = svg; // static, trusted icon markup only
  span.style.display = "inline-flex";
  return span;
}

export class WidgetUI {
  private launcher!: HTMLButtonElement;
  private panel!: HTMLDivElement;
  private messages!: HTMLDivElement;
  private quick!: HTMLDivElement;
  private input!: HTMLTextAreaElement;
  private sendBtn!: HTMLButtonElement;
  private attachBtn!: HTMLButtonElement;
  private fileInput!: HTMLInputElement;
  private typingEl: HTMLDivElement | null = null;
  private expandBtn!: HTMLButtonElement;
  private open = false;

  constructor(
    private shadow: ShadowRoot,
    private config: WidgetConfig,
    private cb: UICallbacks,
  ) {
    this.build();
  }

  private build(): void {
    const style = el("style");
    style.textContent = buildStyles();
    this.shadow.appendChild(style);

    // Launcher
    this.launcher = el("button", "launcher");
    this.launcher.setAttribute("aria-label", "Open chat");
    this.launcher.appendChild(icon(ICON_CHAT));
    this.launcher.addEventListener("click", () => this.setOpen(true));
    this.shadow.appendChild(this.launcher);

    // Panel
    this.panel = el("div", "panel");
    this.panel.setAttribute("role", "dialog");
    this.panel.setAttribute("aria-label", this.config.title);
    this.panel.appendChild(this.buildHeader());
    this.messages = el("div", "messages");
    this.messages.setAttribute("aria-live", "polite");
    this.panel.appendChild(this.messages);
    this.quick = el("div", "quick hidden");
    this.panel.appendChild(this.quick);
    this.panel.appendChild(this.buildComposer());
    this.shadow.appendChild(this.panel);
  }

  private buildHeader(): HTMLDivElement {
    const header = el("div", "header");
    header.appendChild(icon(ICON_CHAT, "avatar"));

    const meta = el("div", "meta");
    meta.appendChild(el("div", "title", this.config.title));
    const status = el("div", "status");
    status.appendChild(el("span", "dot"));
    status.appendChild(el("span", undefined, "Online · typically replies instantly"));
    meta.appendChild(status);
    header.appendChild(meta);

    const human = el("button", "human", "Talk to a person");
    human.type = "button";
    human.setAttribute("aria-label", "Talk to a live customer service agent");
    human.addEventListener("click", () => this.cb.onRequestHuman());
    header.appendChild(human);

    const newchat = el("button", "newchat");
    newchat.type = "button";
    newchat.title = "Start new chat";
    newchat.setAttribute("aria-label", "Start new chat");
    newchat.appendChild(icon(ICON_NEWCHAT));
    newchat.addEventListener("click", () => this.cb.onNewChat());
    header.appendChild(newchat);

    const expand = el("button", "expand");
    expand.type = "button";
    expand.setAttribute("aria-label", "Expand chat");
    expand.title = "Expand chat"; // native hover tooltip, like the "Start new chat" button
    expand.appendChild(icon(ICON_EXPAND));
    expand.addEventListener("click", () => this.cb.onToggleExpand());
    header.appendChild(expand);
    this.expandBtn = expand;

    const close = el("button", "close");
    close.setAttribute("aria-label", "Close chat");
    close.appendChild(icon(ICON_CLOSE));
    close.addEventListener("click", () => this.setOpen(false));
    header.appendChild(close);
    return header;
  }

  private buildComposer(): HTMLFormElement {
    const form = el("form", "composer");
    this.input = el("textarea");
    this.input.rows = 1;
    this.input.placeholder = this.config.placeholder;
    this.input.setAttribute("aria-label", "Message");
    this.input.addEventListener("input", () => {
      this.autogrow();
      this.cb.onTyping?.();
    });
    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.submit();
      }
    });

    // File picker. Hidden input plus a paperclip, which is what everyone
    // expects and avoids styling a native file control across browsers.
    this.fileInput = el("input") as HTMLInputElement;
    this.fileInput.type = "file";
    this.fileInput.multiple = false;
    this.fileInput.accept = ".jpg,.jpeg,.png,.gif,.webp,.heic,.pdf,.txt,.csv,.xlsx,.xls,.docx,.doc";
    this.fileInput.style.display = "none";
    this.fileInput.addEventListener("change", () => {
      const f = this.fileInput.files?.[0];
      this.fileInput.value = "";
      if (f) this.cb.onSendFile?.(f);
    });

    this.attachBtn = el("button", "attach-btn") as HTMLButtonElement;
    this.attachBtn.type = "button";
    this.attachBtn.title = "Attach a file";
    this.attachBtn.setAttribute("aria-label", "Attach a file");
    this.attachBtn.textContent = "📎";
    this.attachBtn.addEventListener("click", () => this.fileInput.click());

    this.sendBtn = el("button", "send");
    this.sendBtn.type = "submit";
    this.sendBtn.setAttribute("aria-label", "Send");
    this.sendBtn.appendChild(icon(ICON_SEND));

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.submit();
    });
    form.appendChild(this.fileInput);
    form.appendChild(this.attachBtn);
    form.appendChild(this.input);
    form.appendChild(this.sendBtn);
    return form;
  }

  private autogrow(): void {
    this.input.style.height = "auto";
    this.input.style.height = Math.min(this.input.scrollHeight, 104) + "px";
  }

  private submit(): void {
    const text = this.input.value.trim();
    if (!text) return;
    this.input.value = "";
    this.autogrow();
    this.cb.onSend(text);
  }

  // ── Open/close ─────────────────────────────────────
  setOpen(open: boolean): void {
    this.open = open;
    this.panel.classList.toggle("open", open);
    this.launcher.classList.toggle("hidden", open);
    if (open) {
      this.launcher.setAttribute("aria-label", "Close chat");
      setTimeout(() => this.input.focus(), 60);
      this.scrollToBottom();
    }
    this.cb.onToggle(open);
  }

  clearUnread(): void {
    this.launcher.classList.remove("has-unread");
  }

  isOpen(): boolean {
    return this.open;
  }

  /** Toggle the enlarged panel size (Task 6). Swaps the header icon, aria-label, and
   *  hover title to match — expand glyph when collapsed, collapse glyph when expanded. */
  setExpanded(expanded: boolean): void {
    this.panel.classList.toggle("expanded", expanded);
    this.expandBtn.setAttribute("aria-label", expanded ? "Shrink chat" : "Expand chat");
    this.expandBtn.title = expanded ? "Shrink chat" : "Expand chat";
    this.expandBtn.innerHTML = "";
    this.expandBtn.appendChild(icon(expanded ? ICON_COLLAPSE : ICON_EXPAND));
  }

  /** Hide the entire widget (bubble + panel). Used when we hand off to Smartsupp so
   *  only the live-chat surface shows — no overlapping launchers. */
  hideWidget(): void {
    this.open = false;
    this.panel.classList.remove("open");
    this.launcher.classList.add("hidden");
  }

  /** Restore the resting state (bubble visible, panel closed). Used when the visitor
   *  closes the live chat and we return to bot mode. */
  showWidget(): void {
    this.open = false;
    this.panel.classList.remove("open");
    this.launcher.classList.remove("hidden");
  }

  // ── Input enable/disable ───────────────────────────
  setInputEnabled(enabled: boolean): void {
    this.input.disabled = !enabled;
    this.sendBtn.disabled = !enabled;
  }

  focusInput(): void {
    if (this.open) this.input.focus();
  }

  /** Clear the transcript back to empty (used by "Start new chat"). Removes every
   *  message and any open product popup; the controller then re-renders the greeting. */
  resetMessages(): void {
    this.messages.innerHTML = "";
    this.typingEl = null;
    this.panel.querySelector(".pcard-overlay")?.remove();
  }

  // ── Messages ───────────────────────────────────────
  addMessage(role: Role, text: string, id?: string): HTMLDivElement {
    const msg = el("div", "msg " + (role === "user" ? "user" : "bot"));
    // The server id is kept on the node so a retracted message can be pulled
    // back out of a transcript the customer is already looking at.
    if (id) msg.dataset.msgId = id;
    const bubble = el("div", "bubble");
    if (role === "assistant") {
      // Restored bot history: render the final, link-aware HTML directly.
      bubble.innerHTML = linkifyText(text);
    } else {
      bubble.textContent = text;
    }
    msg.appendChild(bubble);
    this.messages.appendChild(msg);
    this.scrollToBottom();
    return msg;
  }

  /** Read receipt under the newest customer message ("Seen"), moved forward as
   *  the agent reads more. Inline-styled so no stylesheet change is needed. */
  markLastCustomerSeen(): void {
    const nodes = this.messages.querySelectorAll(".msg.user");
    const last = nodes[nodes.length - 1];
    if (!last) return;
    this.messages.querySelector(".dts-seen")?.remove();
    const tag = el("div", "dts-seen", "Seen");
    tag.style.cssText = "font-size:10px;color:#94a3b8;text-align:right;margin:2px 6px 0 0;";
    last.appendChild(tag);
  }

  /** An agent retracted something. Replace it rather than vanish it silently —
   *  a message disappearing with no trace reads as a glitch. */
  removeMessage(id: string): void {
    if (!id) return;
    const node = this.messages.querySelector(`[data-msg-id="${CSS.escape(id)}"]`);
    if (!node) return;
    node.innerHTML = "";
    node.className = "msg bot deleted";
    const note = el("div", "bubble deleted-note", "This message was removed.");
    node.appendChild(note);
  }

  /** Show a file the customer or an agent shared, under the latest message. */
  addAttachment(a: { filename: string; mimeType: string; sizeBytes: number; storageKey: string }, mine: boolean): void {
    const msg = el("div", "msg " + (mine ? "user" : "bot"));
    const bubble = el("div", "bubble attach");
    const href = `${this.config.apiBase}/files/${encodeURIComponent(a.storageKey)}`;
    if (a.mimeType.startsWith("image/")) {
      const link = el("a") as HTMLAnchorElement;
      link.href = href;
      link.target = "_blank";
      link.rel = "noopener";
      const img = el("img", "attach-img") as HTMLImageElement;
      img.src = href;
      img.alt = a.filename;
      link.appendChild(img);
      bubble.appendChild(link);
    } else {
      const link = el("a", "attach-file") as HTMLAnchorElement;
      link.href = href;
      link.target = "_blank";
      link.rel = "noopener";
      link.textContent = `📎 ${a.filename}`;
      bubble.appendChild(link);
      bubble.appendChild(el("div", "attach-size", formatBytes(a.sizeBytes)));
    }
    msg.appendChild(bubble);
    this.messages.appendChild(msg);
    this.scrollToBottom();
  }

  /**
   * Ask how the chat went once an agent has closed it. Two buttons and an
   * optional comment — anything longer and nobody answers it.
   */
  showRatingPrompt(onRate: (rating: number, comment?: string) => Promise<void>): void {
    if (this.messages.querySelector(".rating")) return;
    const wrap = el("div", "rating");
    wrap.appendChild(el("div", "rating-q", "How did we do?"));
    const row = el("div", "rating-row");

    const done = (msg: string) => {
      wrap.innerHTML = "";
      wrap.appendChild(el("div", "rating-thanks", msg));
    };

    const mk = (label: string, value: number) => {
      const b = el("button", "rating-btn") as HTMLButtonElement;
      b.type = "button";
      b.textContent = label;
      b.addEventListener("click", () => {
        row.remove();
        if (value > 0) {
          void onRate(value);
          done("Thanks — glad we could help.");
          return;
        }
        // A bad rating is the one worth asking about; a good one is not worth
        // another click.
        const box = el("textarea", "rating-comment") as HTMLTextAreaElement;
        box.rows = 2;
        box.placeholder = "What went wrong? (optional)";
        const send = el("button", "rating-send") as HTMLButtonElement;
        send.type = "button";
        send.textContent = "Send";
        send.addEventListener("click", () => {
          void onRate(value, box.value.trim() || undefined);
          done("Thanks — we'll look into it.");
        });
        wrap.appendChild(box);
        wrap.appendChild(send);
        void onRate(value);
      });
      return b;
    };

    row.appendChild(mk("👍 Good", 1));
    row.appendChild(mk("👎 Not great", -1));
    wrap.appendChild(row);
    this.messages.appendChild(wrap);
    this.scrollToBottom();
  }

  /** Tell the customer a file was refused, in the transcript where they sent it. */
  addSystemError(text: string): void {
    const msg = el("div", "msg bot");
    msg.appendChild(el("div", "bubble sys-error", text));
    this.messages.appendChild(msg);
    this.scrollToBottom();
  }

  /** Badge the launcher when a message arrives while the panel is closed. */
  notifyUnread(): void {
    if (this.isOpen()) return;
    this.launcher.classList.add("has-unread");
  }

  /** Re-render a restored message's product cards (from persisted state) beneath its
   *  bubble — same DOM shape and popup/add-to-cart wiring as the live setProducts path. */
  appendProductCards(msg: HTMLDivElement, products: ProductCard[]): void {
    if (!products.length) return;
    const wrap = el("div", "cards");
    for (const card of products) wrap.appendChild(this.buildProductCard(card));
    wrap.appendChild(this.buildMoreOptionsBtn());
    msg.appendChild(wrap);
  }

  /** "Show me more options" chip shown under a card row. Sends the literal text
   *  through the normal send path (same as quick replies) so the Task 4 prompt
   *  rule picks it up. One-shot: removes itself once clicked. */
  private buildMoreOptionsBtn(): HTMLButtonElement {
    const btn = el("button", "more-options-btn", "Show me more options");
    btn.type = "button";
    btn.addEventListener("click", () => {
      btn.remove();
      this.cb.onSend("Show me more options");
    });
    return btn;
  }

  /** One-click handoff chip shown under a reply that routed the customer to
   *  customer service during business hours (John, 2026-08-20): the reply says
   *  "I can connect you with a live agent right now", and this is that link.
   *  Runs the SAME path as the header "Talk to a person" button, so the contact
   *  gate, presence check and after-hours fallback all still apply. One-shot. */
  private buildAgentOfferBtn(label: string): HTMLButtonElement {
    const btn = el("button", "agent-offer-btn", label);
    btn.type = "button";
    btn.setAttribute("aria-label", "Connect me with a live customer service agent");
    btn.addEventListener("click", () => {
      btn.disabled = true;
      btn.remove();
      this.cb.onRequestHuman();
    });
    return btn;
  }

  addSystemNotice(text: string): void {
    const msg = el("div", "msg system");
    msg.appendChild(el("div", "bubble", text));
    this.messages.appendChild(msg);
    this.scrollToBottom();
  }

  startBotStream(): BotMessageHandle {
    const self = this;
    const msg = el("div", "msg bot");
    const bubble = el("div", "bubble");
    msg.appendChild(bubble);
    this.messages.appendChild(msg);
    this.scrollToBottom();

    let rawText = "";
    let sourcesEl: HTMLDivElement | null = null;
    return {
      el: msg,
      append: (text: string) => {
        rawText += text;
        // Stream as plain text — fast and safe. We swap to linkified HTML in finalize().
        bubble.textContent = rawText;
      },
      finalize: () => {
        if (!rawText) return;
        bubble.innerHTML = linkifyText(rawText);
      },
      setSources: (sources: Source[]) => {
        if (!sources.length) return;
        if (!sourcesEl) {
          sourcesEl = el("div", "sources");
          sourcesEl.appendChild(el("div", "label", "Sources"));
          msg.appendChild(sourcesEl);
        }
        for (const s of sources) {
          if (s.url) {
            const a = el("a");
            a.href = s.url;
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            a.appendChild(icon(ICON_LINK));
            a.appendChild(el("span", undefined, s.title || s.url));
            sourcesEl.appendChild(a);
          } else {
            sourcesEl.appendChild(el("div", undefined, s.title));
          }
        }
      },
      setProducts: (products: ProductCard[]) => {
        if (!products.length) return;
        const wrap = el("div", "cards");
        for (const card of products) wrap.appendChild(self.buildProductCard(card));
        wrap.appendChild(self.buildMoreOptionsBtn());
        msg.appendChild(wrap);
        self.scrollToBottom();
      },
      setAgentOffer: (label: string) => {
        msg.appendChild(self.buildAgentOfferBtn(label));
        self.scrollToBottom();
      },
    };
  }

  /** Build one product card: thumbnail, name, from-price, View details, Add to cart. */
  buildProductCard(card: ProductCard): HTMLDivElement {
    const c = el("div", "card");
    const thumb = el("div", "card-thumb");
    if (card.image) {
      const img = document.createElement("img");
      img.src = card.image;
      img.alt = card.name;
      img.loading = "lazy";
      thumb.appendChild(img);
    }
    c.appendChild(thumb);

    const info = el("div", "card-info");
    info.appendChild(el("div", "card-name", card.name));
    const priceLine = el("div", "card-price");
    if (card.fromListPrice != null) {
      priceLine.appendChild(el("s", "list-price", `$${card.fromListPrice.toFixed(2)}`));
      priceLine.appendChild(el("span", "card-sale", ` On sale: $${card.fromPrice.toFixed(2)}`));
    } else {
      // 2026-09-16: this said "/ case" unconditionally, so a product whose cheapest
      // variant was a single-piece sample rendered "from $0.71 / case" (45018). cards.ts
      // now keeps samples out of fromPrice; say the pack size we actually mean.
      const n = card.fromPackCount;
      priceLine.appendChild(
        document.createTextNode(
          typeof n === "number" && n > 1
            ? `from $${card.fromPrice.toFixed(2)} / case of ${n}`
            : `from $${card.fromPrice.toFixed(2)}`,
        ),
      );
    }
    info.appendChild(priceLine);

    const actions = el("div", "card-actions");
    // Explicit navigation affordance: the card is a link to the product page,
    // and the label says so — clicking anywhere except Add opens that page.
    const view = el("a", "card-view");
    view.href = card.url;
    view.target = "_blank";
    view.rel = "noopener noreferrer";
    view.appendChild(icon(ICON_LINK));
    view.appendChild(el("span", undefined, "View product page"));

    const add = el("button", "card-add");
    add.type = "button";
    add.appendChild(icon(ICON_CART));
    add.appendChild(el("span", undefined, "Add to cart"));
    add.addEventListener("click", (e) => {
      e.stopPropagation();
      this.openProductPopup(card);
    });

    actions.appendChild(view);
    actions.appendChild(add);
    info.appendChild(actions);
    c.appendChild(info);

    // Clicking the card body (not the Add button / View link) opens the product page.
    c.addEventListener("click", (e) => {
      const t = e.target as HTMLElement;
      if (t.closest(".card-add") || t.closest(".card-view")) return;
      window.open(card.url, "_blank", "noopener");
    });
    return c;
  }

  /** Open the case + quantity popup over the panel for one product. */
  openProductPopup(card: ProductCard): void {
    this.panel.querySelector(".pcard-overlay")?.remove();
    const overlay = el("div", "pcard-overlay");
    const pop = el("div", "pcard");

    const head = el("div", "pcard-head");
    head.appendChild(el("div", "pcard-title", card.name));
    const close = el("button", "pcard-close");
    close.type = "button";
    close.setAttribute("aria-label", "Close");
    close.appendChild(icon(ICON_CLOSE));
    close.addEventListener("click", () => overlay.remove());
    head.appendChild(close);
    pop.appendChild(head);

    pop.appendChild(el("div", "pcard-choose", "Choose your case"));
    const list = el("div", "pcard-variants");
    let selected: CardVariant | null = null;
    const rows: HTMLDivElement[] = [];
    card.variants.forEach((v) => {
      const row = el("div", "pcard-variant");
      row.appendChild(el("span", "pcard-vlabel", v.label));
      const priceCell = el("span", "pcard-vprice");
      if (v.listPrice != null) {
        priceCell.appendChild(el("s", "list-price", `$${v.listPrice.toFixed(2)}`));
        priceCell.appendChild(el("span", "card-sale", ` On sale: $${v.price.toFixed(2)}`));
      } else {
        priceCell.appendChild(document.createTextNode(`$${v.price.toFixed(2)}`));
      }
      row.appendChild(priceCell);
      row.addEventListener("click", () => {
        if (selected === v) {
          selected = null;
          row.classList.remove("selected");
        } else {
          selected = v;
          rows.forEach((r) => r.classList.remove("selected"));
          row.classList.add("selected");
        }
        updateTotal();
      });
      rows.push(row);
      list.appendChild(row);
    });
    pop.appendChild(list);

    let qty = 1;
    const qtyRow = el("div", "pcard-qty");
    qtyRow.appendChild(el("span", "pcard-qlabel", "Quantity (cases)"));
    const stepper = el("div", "pcard-stepper");
    const minus = el("button", "pcard-step");
    minus.type = "button";
    minus.setAttribute("aria-label", "Decrease quantity");
    minus.appendChild(icon(ICON_MINUS));
    const qval = el("span", "pcard-qval", "1");
    const plus = el("button", "pcard-step");
    plus.type = "button";
    plus.setAttribute("aria-label", "Increase quantity");
    plus.appendChild(icon(ICON_PLUS));
    minus.addEventListener("click", () => {
      if (qty > 1) {
        qty--;
        qval.textContent = String(qty);
        updateTotal();
      }
    });
    plus.addEventListener("click", () => {
      qty++;
      qval.textContent = String(qty);
      updateTotal();
    });
    stepper.appendChild(minus);
    stepper.appendChild(qval);
    stepper.appendChild(plus);
    qtyRow.appendChild(stepper);
    pop.appendChild(qtyRow);

    const addBtn = el("button", "pcard-add");
    addBtn.type = "button";
    const addLabel = el("span", "pcard-addlabel", "");
    addBtn.appendChild(icon(ICON_CART));
    addBtn.appendChild(addLabel);
    const updateTotal = () => {
      addBtn.disabled = !selected;
      addLabel.textContent = selected
        ? `Add to cart · $${(selected.price * qty).toFixed(2)}`
        : "Add to cart";
    };
    updateTotal();
    addBtn.addEventListener("click", async () => {
      if (!selected) return;
      addBtn.disabled = true;
      addLabel.textContent = "Adding…";
      const res = await this.cb.onAddToCart(card.productId, selected.variantId, qty);
      if (res.ok) {
        this.renderPopupSuccess(pop);
      } else {
        addBtn.disabled = false;
        updateTotal();
        this.renderPopupError(pop, card);
      }
    });
    pop.appendChild(addBtn);

    overlay.appendChild(pop);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) overlay.remove();
    });
    this.panel.appendChild(overlay);
  }

  private renderPopupSuccess(pop: HTMLElement): void {
    pop.innerHTML = "";
    pop.className = "pcard done";
    pop.appendChild(icon(ICON_CHECK, "pcard-check"));
    pop.appendChild(el("div", "pcard-title", "Added to cart"));
    const actions = el("div", "pcard-doneactions");
    const viewCart = el("a", "pcard-viewcart", "View cart");
    viewCart.href = "/cart.php";
    viewCart.target = "_top";
    const keep = el("button", "pcard-keep", "Keep shopping");
    keep.type = "button";
    keep.addEventListener("click", () => pop.closest(".pcard-overlay")?.remove());
    actions.appendChild(viewCart);
    actions.appendChild(keep);
    pop.appendChild(actions);
  }

  private renderPopupError(pop: HTMLElement, card: ProductCard): void {
    if (pop.querySelector(".pcard-err")) return;
    const err = el("div", "pcard-err", "Couldn't add that just now. ");
    const link = el("a", "pcard-openpage", "Open product page");
    link.href = card.url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    err.appendChild(link);
    pop.appendChild(err);
  }

  // ── Typing indicator ───────────────────────────────
  showTyping(): void {
    if (this.typingEl) return;
    const msg = el("div", "msg bot");
    const typing = el("div", "typing");
    typing.appendChild(el("span"));
    typing.appendChild(el("span"));
    typing.appendChild(el("span"));
    msg.appendChild(typing);
    this.messages.appendChild(msg);
    this.typingEl = msg;
    this.scrollToBottom();
  }

  hideTyping(): void {
    if (this.typingEl) {
      this.typingEl.remove();
      this.typingEl = null;
    }
  }

  // ── Quick replies ──────────────────────────────────
  setQuickReplies(items: string[]): void {
    this.quick.innerHTML = "";
    for (const label of items) {
      const btn = el("button", undefined, label);
      btn.type = "button";
      btn.addEventListener("click", () => this.cb.onSend(label));
      this.quick.appendChild(btn);
    }
  }

  showQuickReplies(): void {
    if (this.quick.children.length) this.quick.classList.remove("hidden");
  }

  hideQuickReplies(): void {
    this.quick.classList.add("hidden");
  }

  // ── Escalation: email-capture form ─────────────────
  addEscalateForm(opts: EscalateFormOptions): void {
    const msg = el("div", "msg bot");
    const form = el("form", "escalate");
    form.appendChild(el("div", "lead", opts.lead));

    const emailField = el("div", "field");
    const emailLabel = el("label", undefined, "Email");
    const emailInput = el("input");
    emailInput.type = "email";
    emailInput.required = true;
    emailInput.placeholder = "you@company.com";
    emailField.appendChild(emailLabel);
    emailField.appendChild(emailInput);

    const nameField = el("div", "field");
    nameField.appendChild(el("label", undefined, "Name (optional)"));
    const nameInput = el("input");
    nameInput.type = "text";
    nameInput.placeholder = "Your name";
    nameField.appendChild(nameInput);

    const msgField = el("div", "field");
    msgField.appendChild(el("label", undefined, "How can we help?"));
    const msgInput = el("textarea");
    msgInput.value = opts.defaultMessage;
    msgField.appendChild(msgInput);

    const err = el("div", "err");
    err.style.display = "none";

    const submit = el("button", "submit", "Send message");
    submit.type = "submit";

    form.appendChild(emailField);
    form.appendChild(nameField);
    form.appendChild(msgField);
    form.appendChild(err);
    form.appendChild(submit);

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const email = emailInput.value.trim();
      const name = nameInput.value.trim();
      const message = msgInput.value.trim();
      if (!EMAIL_RE.test(email)) {
        err.textContent = "Please enter a valid email address.";
        err.style.display = "block";
        return;
      }
      err.style.display = "none";
      submit.disabled = true;
      submit.textContent = "Sending…";
      const ok = await opts.onSubmit({ email, name, message });
      if (ok) {
        this.renderEscalateSuccess(form, email);
      } else {
        submit.disabled = false;
        submit.textContent = "Send message";
        err.textContent = "Couldn't send that just now. Please try again.";
        err.style.display = "block";
      }
    });

    msg.appendChild(form);
    this.messages.appendChild(msg);
    this.scrollToBottom();
    setTimeout(() => emailInput.focus(), 60);
  }

  // ── Pre-handoff contact form (2026-08-12) ──────────
  /** Name + email (+ optional order #) before we ring a person, so every live
   *  chat can be followed up. Reuses the escalate form's styling. */
  addContactForm(opts: ContactFormOptions): void {
    const msg = el("div", "msg bot");
    const form = el("form", "escalate");
    form.appendChild(el("div", "lead", opts.lead));

    const nameField = el("div", "field");
    nameField.appendChild(el("label", undefined, "Name"));
    const nameInput = el("input");
    nameInput.type = "text";
    nameInput.required = true;
    nameInput.placeholder = "Your name";
    nameField.appendChild(nameInput);

    const emailField = el("div", "field");
    emailField.appendChild(el("label", undefined, "Email"));
    const emailInput = el("input");
    emailInput.type = "email";
    emailInput.required = true;
    emailInput.placeholder = "you@company.com";
    emailField.appendChild(emailInput);

    const orderField = el("div", "field");
    orderField.appendChild(el("label", undefined, "Order # (optional)"));
    const orderInput = el("input");
    orderInput.type = "text";
    orderInput.placeholder = "e.g. 132099";
    orderField.appendChild(orderInput);

    const err = el("div", "err");
    err.style.display = "none";

    const submit = el("button", "submit", "Connect me with a person");
    submit.type = "submit";

    form.appendChild(nameField);
    form.appendChild(emailField);
    form.appendChild(orderField);
    form.appendChild(err);
    form.appendChild(submit);

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const name = nameInput.value.trim();
      const email = emailInput.value.trim();
      if (!name) {
        err.textContent = "Please enter your name.";
        err.style.display = "block";
        return;
      }
      if (!EMAIL_RE.test(email)) {
        err.textContent = "Please enter a valid email address.";
        err.style.display = "block";
        return;
      }
      err.style.display = "none";
      submit.disabled = true;
      submit.textContent = "Connecting…";
      const ok = await opts.onSubmit({ name, email, orderRef: orderInput.value.trim() });
      if (ok) {
        msg.remove(); // the caller shows the "connecting you" notice next
      } else {
        submit.disabled = false;
        submit.textContent = "Connect me with a person";
        err.textContent = "Couldn't start the chat just now. Please try again.";
        err.style.display = "block";
      }
    });

    msg.appendChild(form);
    this.messages.appendChild(msg);
    this.scrollToBottom();
    setTimeout(() => nameInput.focus(), 60);
  }

  // ── Account signup form (in-chat account creation) ──
  addSignupForm(opts: SignupFormOptions): void {
    const msg = el("div", "msg bot");
    const form = el("form", "escalate"); // reuse the escalate form's styling
    form.appendChild(el("div", "lead", opts.lead));

    const field = (labelText: string, input: HTMLInputElement): HTMLDivElement => {
      const f = el("div", "field");
      f.appendChild(el("label", undefined, labelText));
      f.appendChild(input);
      return f;
    };

    const firstInput = el("input");
    firstInput.type = "text";
    firstInput.required = true;
    firstInput.autocomplete = "given-name";
    firstInput.placeholder = "First name";

    const lastInput = el("input");
    lastInput.type = "text";
    lastInput.required = true;
    lastInput.autocomplete = "family-name";
    lastInput.placeholder = "Last name";

    const emailInput = el("input");
    emailInput.type = "email";
    emailInput.required = true;
    emailInput.autocomplete = "email";
    emailInput.placeholder = "you@company.com";

    const companyInput = el("input");
    companyInput.type = "text";
    companyInput.autocomplete = "organization";
    companyInput.placeholder = "Company";

    const phoneInput = el("input");
    phoneInput.type = "tel";
    phoneInput.autocomplete = "tel";
    phoneInput.placeholder = "Phone";

    const err = el("div", "err");
    err.style.display = "none";

    const submit = el("button", "submit", "Create my account");
    submit.type = "submit";

    form.appendChild(field("First name", firstInput));
    form.appendChild(field("Last name", lastInput));
    form.appendChild(field("Email", emailInput));
    form.appendChild(field("Company (optional)", companyInput));
    form.appendChild(field("Phone (optional)", phoneInput));
    form.appendChild(err);
    form.appendChild(submit);

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const firstName = firstInput.value.trim();
      const lastName = lastInput.value.trim();
      const email = emailInput.value.trim();
      if (!firstName || !lastName) {
        err.textContent = "Please enter your first and last name.";
        err.style.display = "block";
        return;
      }
      if (!EMAIL_RE.test(email)) {
        err.textContent = "Please enter a valid email address.";
        err.style.display = "block";
        return;
      }
      err.style.display = "none";
      submit.disabled = true;
      submit.textContent = "Creating your account…";
      const result = await opts.onSubmit({
        firstName,
        lastName,
        email,
        company: companyInput.value.trim(),
        phone: phoneInput.value.trim(),
      });
      if (result.ok) {
        this.renderSignupSuccess(form, email, result);
      } else {
        submit.disabled = false;
        submit.textContent = "Create my account";
        err.textContent =
          "Couldn't create the account just now. Please try again, or call us at 800-615-5822 and we'll set it up by phone.";
        err.style.display = "block";
      }
    });

    msg.appendChild(form);
    this.messages.appendChild(msg);
    this.scrollToBottom();
    setTimeout(() => firstInput.focus(), 60);
  }

  private renderSignupSuccess(
    form: HTMLFormElement,
    email: string,
    result: import("./types").SignupResult,
  ): void {
    form.innerHTML = "";
    form.className = "escalate done";
    form.appendChild(icon(ICON_CHECK, "check"));
    if (result.existing) {
      form.appendChild(el("div", "lead", "Good news — you already have an account with us."));
      form.appendChild(
        el(
          "div",
          "note",
          result.emailSent
            ? "We just sent a password reset link to " +
                email +
                " so you can get back into it. If you don't see it in a few minutes, check your spam folder."
            : "Use the Forgot Password link on the sign-in page to get back into it, or call us at 800-615-5822.",
        ),
      );
    } else {
      form.appendChild(el("div", "lead", "Your account is created."));
      form.appendChild(
        el(
          "div",
          "note",
          result.emailSent
            ? "We sent a password setup link to " +
                email +
                ". Click it to choose your password and you're all set. If you don't see it in a few minutes, check your spam folder."
            : "To set your password, use the Forgot Password link on the sign-in page and we'll email you a setup link.",
        ),
      );
    }
    this.scrollToBottom();
  }

  private renderEscalateSuccess(form: HTMLFormElement, email: string): void {
    form.innerHTML = "";
    form.className = "escalate done";
    form.appendChild(icon(ICON_CHECK, "check"));
    form.appendChild(el("div", "lead", "Sent — your message is in our customer service inbox."));
    form.appendChild(
      el(
        "div",
        "note",
        "Our team will follow up at " +
          email +
          " by the next business day. No need to send a separate email.",
      ),
    );
    this.scrollToBottom();
  }

  scrollToBottom(): void {
    // rAF so layout settles before we measure scrollHeight.
    requestAnimationFrame(() => {
      this.messages.scrollTop = this.messages.scrollHeight;
    });
  }
}
